import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    Receipt, ChevronRight, ChevronDown, RefreshCw, Download, Loader2, AlertTriangle,
    Filter, X, Eye, Search,
} from 'lucide-react';
import CustomDatePicker from '../../components/CustomDatePicker';
import { buildVendorInvoiceUrl } from '../../config/s3Config';

import {
    fetchVendorsByType, fetchCCByVendor, fetchPOForInvoiceReport, fetchVendorInvoiceReport, fetchInvoiceDetail,
    clearVendorList, clearCCList, clearPOList, clearVendorInvoiceReport, clearInvoiceDetail, resetVendorInvoiceReport,
    selectVIRVendorList, selectVIRVendorListLoading,
    selectVIRCCList, selectVIRCCListLoading,
    selectVIRPOList, selectVIRPOListLoading,
    selectVIRReport, selectVIRReportLoading, selectVIRReportError,
    selectVIRDetail, selectVIRDetailLoading,
} from '../../slices/ReportsSlice/vendorInvoiceReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const VENDOR_TYPES = [
    { Type: 'Service Provider', Name: 'Service Provider' },
    { Type: 'Supplier', Name: 'Supplier' },
];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toDDMonYYYY = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const num = (v) => parseFloat(v || 0) || 0;
const money = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// GetVendorsbyType's VendorName column is literally `VendorName + ',' + VendorCode`
// (confirmed in ReportsDataLayer.GetVendorsbyType), so the raw value isn't a display
// name — split off the trailing code before rendering it.
const cleanVendorName = (raw) => (raw || '').split(',')[0].trim();

// spGetVendorInvoiceCC's CC_Name column is `CCCode + ',' + CC_Name` — same pattern,
// reversed order (code first). Strip the leading code before rendering.
const cleanCCName = (raw) => {
    const idx = (raw || '').indexOf(',');
    return idx === -1 ? raw : raw.slice(idx + 1).trim();
};

// ── Compact multi-select dropdown (mirrors the "select all + checklist" jQuery multiselect
// legacy uses for the CC and PO listboxes) ──────────────────────────────────────────────
const MultiSelectDropdown = ({ label, options, valueKey, textKey, selected, onChange, disabled, loading }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const allSelected = options.length > 0 && selected.length === options.length;
    const toggleAll = () => onChange(allSelected ? [] : options.map((o) => o[valueKey]));
    const toggleOne = (val) => onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);

    return (
        <div className="relative" ref={ref}>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
            <button type="button" disabled={disabled || loading}
                onClick={() => setOpen((o) => !o)}
                className={`${inputCls} flex items-center justify-between text-left disabled:opacity-60 disabled:cursor-not-allowed`}>
                <span className="truncate">
                    {loading ? 'Loading…' : selected.length === 0 ? '-Please Select-' : `${selected.length} selected`}
                </span>
                <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            </button>
            {open && !disabled && (
                <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                    {options.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-400">No options</div>
                    ) : (
                        <>
                            <label className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                                <input type="checkbox" className="accent-indigo-600" checked={allSelected} onChange={toggleAll} />
                                Select All
                            </label>
                            {options.map((o) => (
                                <label key={o[valueKey]} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                                    <input type="checkbox" className="accent-indigo-600"
                                        checked={selected.includes(o[valueKey])} onChange={() => toggleOne(o[valueKey])} />
                                    <span className="truncate">{o[textKey]}</span>
                                </label>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Invoice detail modal — tax/other-charges/deductions/payments breakdown.
// Backed by GetVendorInvoiceDetailSummary (Service Provider) / GetSupplierInvoiceSummary
// (Supplier). Legacy's further nested "Show PO Details" modal has no equivalent
// backend endpoint for the Service Provider branch, so it's skipped for both branches
// here rather than building it only for Supplier. ──────────────────────────────────
const DetailModal = ({ vendorType, detail, loading, onClose }) => {
    const isSPPO = vendorType === 'Service Provider';
    const data = isSPPO ? detail?.SPPOData : detail?.SupplierData;
    const bankPayList = detail?.BankPayList || [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0">
                    <h2 className="text-sm font-bold text-white">Invoice Detail{data?.InvoiceNo ? ` — ${data.InvoiceNo}` : ''}</h2>
                    <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
                </div>
                <div className="p-4 space-y-4 text-xs">
                    {loading ? (
                        <div className="flex items-center justify-center gap-3 py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /><span className="text-gray-500">Loading…</span></div>
                    ) : !data ? (
                        <p className="text-center text-gray-400 py-8">No detail found.</p>
                    ) : (
                        <>
                            {isSPPO && data.TaxApplicable === 'Yes' && (
                                <div>
                                    <h3 className="font-bold text-gray-700 dark:text-gray-300 mb-1.5">Taxes</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-gray-50 dark:bg-gray-900/30 rounded-lg p-2.5">
                                        {data.Statecheck ? (
                                            <>
                                                <div><div className="text-gray-400">Account Head</div><div>{data.Taxdcas}</div></div>
                                                <div><div className="text-gray-400">CGST</div><div>{data.Cgstsdca} — {money(data.Cgstsdcaamt)}</div></div>
                                                <div><div className="text-gray-400">SGST</div><div>{data.Sgstsdca} — {money(data.Sgstsdcaamt)}</div></div>
                                                <div><div className="text-gray-400">Total Tax</div><div className="font-semibold">{money(data.TaxTotal)}</div></div>
                                            </>
                                        ) : (
                                            <>
                                                <div><div className="text-gray-400">Account Head</div><div>{data.Taxdcas}</div></div>
                                                <div><div className="text-gray-400">IGST</div><div>{data.Igstsdca} — {money(data.Igstsdcaamt)}</div></div>
                                                <div><div className="text-gray-400">Total Tax</div><div className="font-semibold">{money(data.TaxTotal)}</div></div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {!isSPPO && (
                                <div>
                                    <h3 className="font-bold text-gray-700 dark:text-gray-300 mb-1.5">Transportation</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-gray-50 dark:bg-gray-900/30 rounded-lg p-2.5">
                                        <div><div className="text-gray-400">DCA / Sub DCA</div><div>{data.TransportDCA} / {data.TransportSubDCA}</div></div>
                                        <div><div className="text-gray-400">Amount</div><div>{money(data.TransportAmount)}</div></div>
                                        <div><div className="text-gray-400">CGST</div><div>{data.TransportCGSTpercent}% — {money(data.TransportCGSTAmt)}</div></div>
                                        <div><div className="text-gray-400">SGST</div><div>{data.TransportSGSTpercent}% — {money(data.TransportSGSTAmt)}</div></div>
                                        <div><div className="text-gray-400">IGST</div><div>{data.TransportIGSTpercent}% — {money(data.TransportIGSTAmt)}</div></div>
                                    </div>
                                </div>
                            )}

                            {(data.OtherChargeList || []).length > 0 && (
                                <div>
                                    <h3 className="font-bold text-gray-700 dark:text-gray-300 mb-1.5">Other Charges</h3>
                                    <table className="min-w-full text-[11px] divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-100 dark:bg-gray-900/40">
                                            <tr>{['CC', 'Account Head', 'Sub Account Head', 'Amount'].map((h) => <th key={h} className="px-2 py-1 text-left font-bold text-gray-500 uppercase">{h}</th>)}</tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {data.OtherChargeList.map((r, i) => (
                                                <tr key={i}><td className="px-2 py-1">{r.CCCode}</td><td className="px-2 py-1">{r.DCACode}</td><td className="px-2 py-1">{r.SubDCACode}</td><td className="px-2 py-1 text-right">{money(r.Amount)}</td></tr>
                                            ))}
                                        </tbody>
                                        <tfoot><tr className="font-bold bg-gray-50 dark:bg-gray-900/30"><td colSpan={3} className="px-2 py-1 text-right">Total</td><td className="px-2 py-1 text-right">{money(data.ChargesTotal)}</td></tr></tfoot>
                                    </table>
                                </div>
                            )}

                            {(data.DeductionList || []).length > 0 && (
                                <div>
                                    <h3 className="font-bold text-gray-700 dark:text-gray-300 mb-1.5">Deductions</h3>
                                    <table className="min-w-full text-[11px] divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-100 dark:bg-gray-900/40">
                                            <tr>{['CC', 'Account Head', 'Sub Account Head', 'Amount'].map((h) => <th key={h} className="px-2 py-1 text-left font-bold text-gray-500 uppercase">{h}</th>)}</tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {data.DeductionList.map((r, i) => (
                                                <tr key={i}><td className="px-2 py-1">{r.CCCode}</td><td className="px-2 py-1">{r.DCACode}</td><td className="px-2 py-1">{r.SubDCACode}</td><td className="px-2 py-1 text-right">{money(r.Amount)}</td></tr>
                                            ))}
                                        </tbody>
                                        <tfoot><tr className="font-bold bg-gray-50 dark:bg-gray-900/30"><td colSpan={3} className="px-2 py-1 text-right">Total</td><td className="px-2 py-1 text-right">{money(data.DeductionTotal)}</td></tr></tfoot>
                                    </table>
                                </div>
                            )}

                            {isSPPO && (
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-2.5"><div className="text-gray-400">Advance</div><div className="font-semibold">{money(data.Advance)}</div></div>
                                    <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-2.5"><div className="text-gray-400">Retention</div><div className="font-semibold">{money(data.Retention)}</div></div>
                                    <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-2.5"><div className="text-gray-400">Hold</div><div className="font-semibold">{money(data.Hold)}</div></div>
                                </div>
                            )}

                            {bankPayList.length > 0 && (
                                <div>
                                    <h3 className="font-bold text-gray-700 dark:text-gray-300 mb-1.5">Payments</h3>
                                    <table className="min-w-full text-[11px] divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-100 dark:bg-gray-900/40">
                                            <tr>{['Paid Date', 'Description', 'Bank Name', 'Mode of Pay', 'Net Paid'].map((h) => <th key={h} className="px-2 py-1 text-left font-bold text-gray-500 uppercase">{h}</th>)}</tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {bankPayList.map((r, i) => (
                                                <tr key={i}>
                                                    <td className="px-2 py-1 whitespace-nowrap">{r.TransactionDate}</td>
                                                    <td className="px-2 py-1">{r.Remarks}</td>
                                                    <td className="px-2 py-1">{r.BankName}</td>
                                                    <td className="px-2 py-1">{r.TransactionType}</td>
                                                    <td className="px-2 py-1 text-right">{money(r.TransactionAmount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot><tr className="font-bold bg-gray-50 dark:bg-gray-900/30"><td colSpan={4} className="px-2 py-1 text-right">Total</td><td className="px-2 py-1 text-right">{money(detail?.BankCashTotal)}</td></tr></tfoot>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const VendorInvoiceReport = () => {
    const dispatch = useDispatch();

    const vendorList = useSelector(selectVIRVendorList);
    const vendorListLoading = useSelector(selectVIRVendorListLoading);
    const ccListRaw = useSelector(selectVIRCCList);
    const ccOptions = useMemo(() => ccListRaw.map((c) => ({ ...c, CC_Name: cleanCCName(c.CC_Name) })), [ccListRaw]);
    const ccListLoading = useSelector(selectVIRCCListLoading);
    const poList = useSelector(selectVIRPOList);
    const poListLoading = useSelector(selectVIRPOListLoading);
    const report = useSelector(selectVIRReport);
    const reportLoading = useSelector(selectVIRReportLoading);
    const reportError = useSelector(selectVIRReportError);
    const detail = useSelector(selectVIRDetail);
    const detailLoading = useSelector(selectVIRDetailLoading);

    const [vendorType, setVendorType] = useState('');
    const [vendorCode, setVendorCode] = useState('');
    const [ccStatus, setCcStatus] = useState('');
    const [selectedCCs, setSelectedCCs] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedPOs, setSelectedPOs] = useState([]);
    const [ranParams, setRanParams] = useState(null);
    const [detailRow, setDetailRow] = useState(null);

    useEffect(() => () => dispatch(resetVendorInvoiceReport()), [dispatch]);

    // Auto-loads PO numbers once vendor + CC(s) + date range are all set — mirrors
    // legacy's BindPOS(), which fired on the To-Date picker's close event.
    useEffect(() => {
        setSelectedPOs([]);
        dispatch(clearPOList());
        if (vendorType && vendorCode && selectedCCs.length > 0 && fromDate && toDate) {
            dispatch(fetchPOForInvoiceReport({
                VendorCode: vendorCode, VendorType: vendorType,
                // spGetPOForInvoiceReport splits @CCCodes with fnsplit(...,',',1), which needs a
                // trailing delimiter to pick up the last code (documented example: 'CC-146,').
                CCCode: `${selectedCCs.join(',')},`, FromDate: toDDMonYYYY(fromDate), ToDate: toDDMonYYYY(toDate),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCCs, fromDate, toDate]);

    const handleVendorTypeChange = (val) => {
        setVendorType(val);
        setVendorCode(''); setCcStatus(''); setSelectedCCs([]); setFromDate(''); setToDate(''); setSelectedPOs([]);
        setRanParams(null);
        dispatch(clearVendorList()); dispatch(clearCCList()); dispatch(clearPOList()); dispatch(clearVendorInvoiceReport());
        if (val) dispatch(fetchVendorsByType({ VendorType: val }));
    };

    const handleVendorChange = (val) => {
        setVendorCode(val);
        setCcStatus(''); setSelectedCCs([]); setFromDate(''); setToDate(''); setSelectedPOs([]);
        setRanParams(null);
        dispatch(clearCCList()); dispatch(clearPOList()); dispatch(clearVendorInvoiceReport());
    };

    const handleCCStatusChange = (val) => {
        setCcStatus(val);
        setSelectedCCs([]); setFromDate(''); setToDate(''); setSelectedPOs([]);
        setRanParams(null);
        dispatch(clearCCList()); dispatch(clearPOList()); dispatch(clearVendorInvoiceReport());
        if (val && vendorType && vendorCode) {
            dispatch(fetchCCByVendor({ VendorCode: vendorCode, VendorType: vendorType, CCStatusval: val }));
        }
    };

    const handleFromDateChange = (val) => {
        setFromDate(val);
        setToDate('');
        setSelectedPOs([]);
        dispatch(clearPOList());
    };

    const handleReset = () => {
        setVendorType(''); setVendorCode(''); setCcStatus(''); setSelectedCCs([]);
        setFromDate(''); setToDate(''); setSelectedPOs([]); setRanParams(null);
        dispatch(resetVendorInvoiceReport());
    };

    const runReport = () => {
        if (!vendorType) { toast.warning('Select Vendor Type'); return; }
        if (!vendorCode) { toast.warning('Select Vendor'); return; }
        if (!ccStatus) { toast.warning('Select Cost Centre Status'); return; }
        if (selectedCCs.length === 0) { toast.warning('Select Cost Centre'); return; }
        if (!fromDate) { toast.warning('Select From Date'); return; }
        if (!toDate) { toast.warning('Select To Date'); return; }
        if (selectedPOs.length === 0) { toast.warning('Select PO No'); return; }

        const params = {
            VendorCode: vendorCode, VendorType: vendorType,
            ReportCC: selectedCCs, ReportPO: selectedPOs,
            FromDate: toDDMonYYYY(fromDate), ToDate: toDDMonYYYY(toDate),
        };
        setRanParams(params);
        dispatch(fetchVendorInvoiceReport(params));
    };

    const handleRefresh = () => { if (ranParams) dispatch(fetchVendorInvoiceReport(ranParams)); };

    const isSPPO = ranParams?.VendorType === 'Service Provider';
    const rows = isSPPO ? (report?.SPPOInvoiceList || []) : (report?.SupplierInvoiceList || []);

    const totals = useMemo(() => rows.reduce((acc, r) => {
        acc.BasicValue += num(r.BasicValue); acc.TaxTotal += num(r.TaxTotal); acc.DeductionTotal += num(r.DeductionTotal);
        acc.OtherTotal += num(r.OtherTotal); acc.PaidTotal += num(r.PaidTotal); acc.Balance += num(r.Balance); acc.NetAmount += num(r.NetAmount);
        return acc;
    }, { BasicValue: 0, TaxTotal: 0, DeductionTotal: 0, OtherTotal: 0, PaidTotal: 0, Balance: 0, NetAmount: 0 }), [rows]);

    const openDetail = (row) => {
        setDetailRow(row);
        dispatch(fetchInvoiceDetail({
            vendorType: ranParams.VendorType,
            VendorCode: ranParams.VendorCode, VendorType: ranParams.VendorType,
            CCCode: row.CCCode, PONo: row.PONo, InvoiceNo: row.InvoiceNo,
            ...(isSPPO ? {} : { MRRNo: row.MRRNo }),
            CompanyGST: row.CompanyGST, VendorGST: row.VendorGST,
        }));
    };
    const closeDetail = () => { setDetailRow(null); dispatch(clearInvoiceDetail()); };

    const handleExport = () => {
        if (rows.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = rows.map((r) => (isSPPO ? {
            'Invoice No': r.InvoiceNo, 'CC Code': r.CCCode, 'PO No': r.PONo, 'Invoice Date': r.InvoiceDate, 'Making Date': r.InvMakingDate,
            Basic: r.BasicValue, Tax: r.TaxTotal, Deduction: r.DeductionTotal, Other: r.OtherTotal, Paid: r.PaidTotal, Balance: r.Balance, Total: r.NetAmount,
            DCA: r.DCACode, 'Sub DCA': r.SubDCACode,
        } : {
            'Invoice No': r.InvoiceNo, 'MRR No': r.MRRNo, 'CC Code': r.CCCode, 'PO No': r.PONo, 'Invoice Date': r.InvoiceDate, 'Making Date': r.InvMakingDate,
            Basic: r.BasicValue, Tax: r.TaxTotal, Deduction: r.DeductionTotal, Other: r.OtherTotal, Paid: r.PaidTotal, Balance: r.Balance, Net: r.NetAmount,
            DCA: r.DCACode, 'Sub DCA': r.SubDCACode,
        }));
        exportRows.push({ 'Invoice No': 'TOTAL', Basic: totals.BasicValue, Tax: totals.TaxTotal, Deduction: totals.DeductionTotal, Other: totals.OtherTotal, Paid: totals.PaidTotal, Balance: totals.Balance, [isSPPO ? 'Total' : 'Net']: totals.NetAmount });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'Vendor Invoices');
        XLSX.writeFile(wb, `Vendor_Invoice_Report_${ranParams?.VendorCode || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    const filtersLocked = !!ranParams;

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Receipt className="h-4 w-4 text-white" />
                            </div>
                            Vendor Invoice Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Invoice-wise tax, deduction and payment summary for a vendor</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Vendor Invoice Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <Filter className="h-3.5 w-3.5 text-indigo-600" />
                        <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                    </div>
                    {filtersLocked && (
                        <button onClick={handleReset} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                            <RefreshCw className="w-3 h-3" /> Change Filters
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Vendor Type</label>
                        <select className={inputCls} value={vendorType} onChange={(e) => handleVendorTypeChange(e.target.value)} disabled={filtersLocked}>
                            <option value="">-Please Select-</option>
                            {VENDOR_TYPES.map((v) => <option key={v.Type} value={v.Type}>{v.Name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Vendor</label>
                        <select className={inputCls} value={vendorCode} onChange={(e) => handleVendorChange(e.target.value)} disabled={!vendorType || vendorListLoading || filtersLocked}>
                            <option value="">-Please Select-</option>
                            {vendorList.map((v) => <option key={v.VendorCode} value={v.VendorCode}>{cleanVendorName(v.VendorName)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost Centre Status</label>
                        <select className={inputCls} value={ccStatus} onChange={(e) => handleCCStatusChange(e.target.value)} disabled={!vendorCode || filtersLocked}>
                            <option value="">-Please Select-</option>
                            <option value="Active">Active</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                    <MultiSelectDropdown label="Cost Centre" options={ccOptions} valueKey="CC_Code" textKey="CC_Name"
                        selected={selectedCCs} onChange={setSelectedCCs} disabled={!ccStatus || filtersLocked} loading={ccListLoading} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mt-3">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">From Date</label>
                        <CustomDatePicker value={fromDate} onChange={handleFromDateChange} maxDate={new Date()} disabled={selectedCCs.length === 0 || filtersLocked} />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">To Date</label>
                        <CustomDatePicker value={toDate} onChange={setToDate} minDate={fromDate || null} maxDate={new Date()} disabled={!fromDate || filtersLocked} />
                    </div>
                    <MultiSelectDropdown label="PO No" options={poList} valueKey="SPPONo" textKey="SPPONoDesc"
                        selected={selectedPOs} onChange={setSelectedPOs} disabled={!toDate || filtersLocked} loading={poListLoading} />
                    <div className="flex gap-2">
                        <button onClick={runReport} disabled={reportLoading || filtersLocked} className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
                            <Search className="w-3.5 h-3.5" /> View
                        </button>
                        <button onClick={handleReset} className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && rows.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found.</div>
            ) : rows.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">{isSPPO ? 'Service Provider' : 'Supplier'} Invoices</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Invoice No</th>
                                    {!isSPPO && <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">MRR No</th>}
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">CC</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">PO No</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Invoice Date</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Making Date</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Basic</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Tax</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Deduction</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Other</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Paid</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Balance</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Total</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">DCA</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Sub DCA</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Docs</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Detail</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {rows.map((row, i) => (
                                    <tr key={row.InvoiceId ?? i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-2 py-1 text-xs whitespace-nowrap font-semibold text-gray-800 dark:text-gray-100">{row.InvoiceNo}</td>
                                        {!isSPPO && <td className="px-2 py-1 text-xs whitespace-nowrap">{row.MRRNo}</td>}
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.CCCode}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.PONo}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.InvoiceDate}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.InvMakingDate}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.BasicValue)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.TaxTotal)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.DeductionTotal)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.OtherTotal)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.PaidTotal)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.Balance)}</td>
                                        <td className="px-2 py-1 text-xs text-right font-semibold whitespace-nowrap">{money(row.NetAmount)}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.DCACode}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.SubDCACode}</td>
                                        <td className="px-2 py-1 text-center">
                                            {row.ViewDocs ? (
                                                <a href={buildVendorInvoiceUrl(row.ViewDocs)} target="_blank" rel="noreferrer"
                                                    className="inline-flex text-indigo-600 hover:text-indigo-800">
                                                    <Search className="w-3.5 h-3.5" />
                                                </a>
                                            ) : null}
                                        </td>
                                        <td className="px-2 py-1 text-center">
                                            <button onClick={() => openDetail(row)} className="inline-flex text-indigo-600 hover:text-indigo-800">
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    <td className="px-2 py-1 text-xs" colSpan={isSPPO ? 6 : 7}>TOTAL</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.BasicValue)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.TaxTotal)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.DeductionTotal)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.OtherTotal)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.PaidTotal)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.Balance)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.NetAmount)}</td>
                                    <td /><td /><td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}

            {detailRow && (
                <DetailModal vendorType={ranParams.VendorType} detail={detail} loading={detailLoading} onClose={closeDetail} />
            )}
        </div>
    );
};

export default VendorInvoiceReport;
